// src/components/home/gallery.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Container,
  Dialog,
  IconButton,
  ImageList,
  ImageListItem,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import theme from "../../styles/theme";

// ✅ Import your images from src/assets/gallery (adjust filenames)
import gallery1 from "../../assets/gallery/gallery-1.jpg";
import gallery2 from "../../assets/gallery/gallery-2.jpg";
import gallery3 from "../../assets/gallery/gallery-3.jpg";
import gallery4 from "../../assets/gallery/gallery-4.jpg";
import gallery5 from "../../assets/gallery/gallery-5.jpg";
import gallery6 from "../../assets/gallery/gallery-6.jpg";
import gallery7 from "../../assets/gallery/gallery-7.jpg";
import gallery8 from "../../assets/gallery/gallery-8.jpg";
import gallery9 from "../../assets/gallery/gallery-9.jpg";
import gallery10 from "../../assets/gallery/gallery-10.jpg";
import { Instagram } from "@mui/icons-material";

const GallerySection = () => {
  const { t } = useTranslation();

  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const columns = isLgUp ? 4 : isMdUp ? 3 : isSmUp ? 2 : 1;
  const gap = isMdUp ? 16 : 12;

  const galleryItems = useMemo(
    () => [
      {
        src: gallery1,
        width: 1080,
        height: 1920,
        title: t("Home.Gallery.Items.1.Title", "Title"),
        subtitle: t("Home.Gallery.Items.1.Subtitle", "Subtitle"),
      },
      {
        src: gallery2,
        title: t("Home.Gallery.Items.2.Title", "Title"),
        subtitle: t("Home.Gallery.Items.2.Subtitle", "Subtitle"),
      },
      {
        src: gallery3,
        title: t("Home.Gallery.Items.3.Title", "Title"),
        subtitle: t("Home.Gallery.Items.3.Subtitle", "Subtitle"),
      },
      {
        src: gallery4,
        title: t("Home.Gallery.Items.4.Title", "Title"),
        subtitle: t("Home.Gallery.Items.4.Subtitle", "Subtitle"),
      },
      {
        src: gallery5,
        title: t("Home.Gallery.Items.5.Title", "Title"),
        subtitle: t("Home.Gallery.Items.5.Subtitle", "Subtitle"),
      },
      {
        src: gallery6,
        title: t("Home.Gallery.Items.6.Title", "Title"),
        subtitle: t("Home.Gallery.Items.6.Subtitle", "Subtitle"),
      },
      {
        src: gallery7,
        width: 372,
        height: 464,
        title: t("Home.Gallery.Items.7.Title", "Title"),
        subtitle: t("Home.Gallery.Items.7.Subtitle", "Subtitle"),
      },
      {
        src: gallery8,
        title: t("Home.Gallery.Items.8.Title", "Title"),
        subtitle: t("Home.Gallery.Items.8.Subtitle", "Subtitle"),
      },
      {
        src: gallery9,
        title: t("Home.Gallery.Items.9.Title", "Title"),
        subtitle: t("Home.Gallery.Items.9.Subtitle", "Subtitle"),
      },
      {
        src: gallery10,
        title: t("Home.Gallery.Items.10.Title", "Title"),
        subtitle: t("Home.Gallery.Items.10.Subtitle", "Subtitle"),
      },
    ],
    [t],
  );

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex(
      (prev) => (prev - 1 + galleryItems.length) % galleryItems.length,
    );
  }, [galleryItems.length]);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % galleryItems.length);
  }, [galleryItems.length]);

  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, closeLightbox, goPrev, goNext]);

  const activeItem = galleryItems[activeIndex];

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="xl">
        <Chip
          label={t("Home.Products.ChipTitle", "Vårt arbeid")}
          color="secondary"
          sx={{
            marginBottom: 1.5,
          }}
        />
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography variant="h3" fontWeight={600} sx={{ mb: 1 }}>
            {t("Home.Gallery.Title", "Galleri")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.8, maxWidth: 780, mx: "auto" }}
          >
            {t(
              "Home.Gallery.Subtitle",
              "Et lite utvalg av tidligere arbeid — tekstiltrykk, profilprodukter og design.",
            )}
          </Typography>
        </Box>

        <ImageList variant="masonry" cols={columns} gap={gap} sx={{ m: 0 }}>
          {galleryItems.map((item, index) => (
            <ImageListItem
              data-aos="fade in"
              data-aos-delay={index * 70}
              key={`${item.title}-${index}`}
              sx={{ overflow: "hidden", borderRadius: 4 }}
            >
              <Box
                onClick={() => openLightbox(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openLightbox(index);
                }}
                sx={{
                  position: "relative",
                  aspectRatio: `${item.width || 1440} / ${item.height || 1920}`,
                  cursor: "pointer",
                  borderRadius: 4,
                  overflow: "hidden",
                  outline: "none",
                  "&:focus-visible": {
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
                  },

                  "&:hover .galleryImage": {
                    transform: "scale(1.06)",
                  },
                  "&:hover .galleryOverlay": {
                    opacity: 1,
                  },
                  "&:hover .galleryCaption": {
                    transform: "translateY(0px)",
                    opacity: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.src}
                  width={item.width || 1440}
                  height={item.height || 1920}
                  alt={item.title}
                  loading="lazy"
                  className="galleryImage"
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    transform: "scale(1)",
                    transition: "transform 220ms ease",
                    willChange: "transform",
                    backgroundColor: "#fff",
                  }}
                />

                {/* Overlay */}
                <Box
                  className="galleryOverlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    transition: "opacity 220ms ease",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0.15) 55%, rgba(0,0,0,0))",
                  }}
                />

                {/* Caption */}
                <Box
                  className="galleryCaption"
                  sx={{
                    position: "absolute",
                    left: 16,
                    right: 16,
                    bottom: 14,
                    color: "#fff",
                    transform: "translateY(8px)",
                    opacity: 0,
                    transition: "transform 220ms ease, opacity 220ms ease",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={900}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {item.subtitle}
                  </Typography>
                </Box>
              </Box>
            </ImageListItem>
          ))}
        </ImageList>

        {/* Lightbox */}
        <Dialog
          open={isLightboxOpen}
          onClose={closeLightbox}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              backgroundColor: "rgba(0,0,0,0.92)",
              borderRadius: 4,
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ position: "relative", p: { xs: 1.5, sm: 2.5 } }}>
            {/* Close */}
            <IconButton
              onClick={closeLightbox}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                zIndex: 3,
              }}
              aria-label={t("Home.Gallery.Close", "Lukk")}
            >
              <CloseIcon />
            </IconButton>

            {/* Prev */}
            <IconButton
              onClick={goPrev}
              sx={{
                position: "absolute",
                top: "50%",
                left: 10,
                transform: "translateY(-50%)",
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                zIndex: 3,
              }}
              aria-label={t("Home.Gallery.Prev", "Forrige")}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            {/* Next */}
            <IconButton
              onClick={goNext}
              sx={{
                position: "absolute",
                top: "50%",
                right: 10,
                transform: "translateY(-50%)",
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                zIndex: 3,
              }}
              aria-label={t("Home.Gallery.Next", "Neste")}
            >
              <ArrowForwardIosIcon />
            </IconButton>

            {/* Image */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: { xs: "52vh", md: "70vh" },
              }}
            >
              <Box
                component="img"
                src={activeItem?.src}
                alt={activeItem?.title}
                sx={{
                  maxWidth: "100%",
                  maxHeight: { xs: "52vh", md: "70vh" },
                  objectFit: "contain",
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.04)",
                }}
              />
            </Box>

            {/* Caption */}
            <Box sx={{ pt: 1.25, textAlign: "center" }}>
              <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
                {activeItem?.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                {activeItem?.subtitle}
              </Typography>
            </Box>
          </Box>
        </Dialog>
        <Link
          href="https://www.instagram.com/forsteinntrykk/"
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            display: "inline-flex",
            justifyContent: "center",
            width: "100%",
            color: "text.subtle",
            opacity: 0.8,
            mt: 3,
            transition: "color 180ms ease, opacity 180ms ease",
            "&:hover": {
              color: "primary.main",
              opacity: 1,
            },
          }}
        >
          <Typography
            variant="body1"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ maxWidth: 780, mx: "auto" }}
          >
            <Instagram sx={{ fontSize: "1.25em" }} />
            {t("Home.Gallery.Instagram", "Følg oss på Instagram for mer")}
          </Typography>
        </Link>
      </Container>
    </Box>
  );
};

export default GallerySection;
